/**
 * ============================================================================
 * Proposal Data Transformers - Single Source of Truth
 * ============================================================================
 *
 * This file consolidates all proposal-related data transformations:
 * - Backend API responses → Form data (for loading/editing proposals)
 * - Form data → API requests (for calculations and saving)
 * - API calculation responses → UI display models
 * - Taxonomy conversions and utilities
 *
 * @module proposalTransformers
 */

import { addDays, format, isAfter, startOfDay } from "date-fns";
import {
  ProposalFormData,
  SaveProposalRequestDto,
  TaxonomyTaxonomyState,
  ProcessedTaxonomyState,
  ByCodeStateByTaxonomy,
  ByIdStateByTaxonomy,
  TaxonomyGroupState,
} from "library/types/types";
import { CalculationRequestDto } from "state/queries/useCalculations";
import { TFunction } from "i18next";
import { genUUID } from "library/utils";
import { UserInfo } from "store";

// ============================================================================
// SECTION 1: Taxonomy Helper Functions
// ============================================================================

/**
 * Creates taxonomy group state from raw taxonomy data
 * Handles both array format and already-processed TaxonomyGroupState objects
 */
const createTaxonomyGroupsState = (taxonomies: {
  [key: string]: any;
}): TaxonomyGroupState => {
  let retVal = {} as TaxonomyGroupState;
  Object.entries(taxonomies).forEach(([key, taxonomyGroup]) => {
    // Check if taxonomyGroup is already a TaxonomyGroupState object
    if (
      taxonomyGroup &&
      typeof taxonomyGroup === "object" &&
      "byId" in taxonomyGroup &&
      "byCode" in taxonomyGroup
    ) {
      // Already processed, use as-is
      retVal[key] = taxonomyGroup;
    } else if (Array.isArray(taxonomyGroup)) {
      // Process array into TaxonomyGroupState structure
      const taxonomyStateByTaxonomy = {
        byId: taxonomyGroup.reduce((byId: any, t: any) => {
          byId[t.id] = t;
          return byId;
        }, {} as ByIdStateByTaxonomy),
        byCode: taxonomyGroup.reduce((byCode: any, t: any) => {
          byCode[t.code] = t;
          return byCode;
        }, {} as ByCodeStateByTaxonomy),
        items: taxonomyGroup,
      };
      retVal[key] = taxonomyStateByTaxonomy;
    }
  });
  return retVal;
};

/**
 * Converts taxonomy code to ID
 * @returns Taxonomy ID or null if not found
 */
export const getTaxonomyIdFromCode = (
  taxonomy: TaxonomyTaxonomyState | ProcessedTaxonomyState | undefined | null,
  code: string | undefined,
  taxonomyType: string
): number | null => {
  if (!code || !taxonomy) return null;
  const tgs = createTaxonomyGroupsState(taxonomy);

  // If already numeric ID, return as number
  if (!isNaN(Number(code)) && code !== "") return Number(code);

  // Try to find by code in the specified taxonomy type
  const item = tgs[taxonomyType]?.byCode?.[code];
  return item ? item.id : null;
};

/**
 * Converts taxonomy ID to code
 * @returns Taxonomy code or null if not found
 */
export const getTaxonomyCodeFromId = (
  taxonomy: TaxonomyTaxonomyState | ProcessedTaxonomyState | undefined | null,
  id: number | undefined | null,
  taxonomyType: string
): string | null => {
  if (!id || !taxonomy) return null;

  // Try to find by id in the specified taxonomy type
  const item = taxonomy[taxonomyType]?.byId?.[id];
  return item ? item.code : null;
};

// ============================================================================
// SECTION 2: Utility Functions
// ============================================================================

/**
 * Safe string conversion - handles null/undefined
 */
const safeString = (value: any): string => {
  return value != null ? String(value) : "";
};

/**
 * Safe boolean conversion
 */
const safeBoolean = (value: any): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    return value.toLowerCase() === "true" || value === "1";
  }
  return Boolean(value);
};

/**
 * Safe number conversion - returns null for invalid numbers
 */
const safeNumber = (
  value: string | number | null | undefined
): number | null => {
  if (value == null) return null;
  const trimmed = String(value).trim();
  if (!trimmed || isNaN(Number(trimmed))) return null;
  return Number(trimmed);
};

/**
 * Format date from ISO string to YYYY-MM-DD format
 * Handles dates without timezone by treating them as local dates
 */
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return "";
  try {
    // If the date string doesn't have timezone info (no Z or +/-), treat it as a local date
    // by just extracting the date part directly to avoid timezone conversion issues
    if (dateString.match(/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2})?$/)) {
      // Date without timezone - extract date part directly
      const datePart = dateString.split("T")[0];
      return datePart || "";
    }
    // Date with timezone - convert properly
    const date = new Date(dateString);
    return date.toISOString().split("T")[0] || "";
  } catch {
    return "";
  }
};

/**
 * Convert form date to ISO string
 * Handles dates by treating them as local dates at midnight to avoid timezone shifts
 */
const formatDateToISO = (dateString: string): string | null => {
  if (!dateString?.trim()) return null;
  try {
    // If it's a simple date format (YYYY-MM-DD), parse it as a local date
    // to avoid timezone conversion issues
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const parts = dateString.split("-");
      if (parts.length === 3) {
        const year = parseInt(parts[0]!, 10);
        const month = parseInt(parts[1]!, 10);
        const day = parseInt(parts[2]!, 10);

        if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
          const date = new Date(year, month - 1, day, 0, 0, 0, 0);
          return date.toISOString();
        }
      }
    }
    // For other formats, use default Date parsing
    const date = new Date(dateString);
    return date.toISOString();
  } catch {
    return null;
  }
};

// ============================================================================
// SECTION 3: Backend Response → Form Data (Loading/Editing)
// ============================================================================

/**
 * Maps OfferListEntryDto from backend API to ProposalFormData for form population
 * Used when loading an existing proposal/offer for editing
 *
 * @param offer - The offer object from backend API
 * @param taxonomy - Optional taxonomy for ID-to-code conversion
 * @returns ProposalFormData suitable for react-hook-form
 */
export const mapOfferToProposalForm = (
  offer: any,
  taxonomy?: TaxonomyTaxonomyState | ProcessedTaxonomyState | null
): ProposalFormData => {
  // Extract data from the backend response structure
  const request = offer.Offer?.Request;
  const client = request?.Client;
  const vehicle = request?.Vehicle;
  const owner = vehicle?.Owner;
  const insurance = request?.Insurance;
  const selectedProducts = request?.SelectedProducts || [];

  // Determine insurance product from SelectedProducts array
  const insuranceProduct =
    selectedProducts.length > 0 ? selectedProducts[0].toUpperCase() : "";

  // Convert OfferStatus ID to code if taxonomy is available
  const offerStatusCode =
    taxonomy && offer.OfferStatus != null
      ? getTaxonomyCodeFromId(taxonomy, offer.OfferStatus, "OfferStatus") || ""
      : "";

  return {
    proposalData: {
      offerId: safeString(offer.OfferId), // Store offer ID in form state
      proposalNumber: safeString(offer.OfferId),
      insuranceProduct: insuranceProduct,
      proposalStatus: "",
      offerStatus: offerStatusCode,
      proposalType: insurance?.Renewal ? "renewal" : "new",
      bulkTask: "",
      proposalTemplate: "",
      rejectionReason: undefined,
      webTemplate: false,
    },
    vehicleData: {
      licensePlateNumber: safeString(vehicle?.LicensePlate),
      vehicleMake: safeString(vehicle?.Brand),
      vehicleModel: safeString(vehicle?.Model),
      productionYear: safeString(vehicle?.ManufactureYear),
      isNew: safeBoolean(vehicle?.IsNew),
      vin: safeString(vehicle?.Vin),
      objectGroup: safeString(vehicle?.VehicleLegalTypeId),
      objectGroupType: safeString(
        vehicle?.VehicleTypeId || vehicle?.TypeID || vehicle?.TypeName
      ),
      engineCapacity: safeString(vehicle?.CubicCapacityCcm),
      power: safeString(vehicle?.PowerKw),
      maximumGrossWeight: safeString(
        vehicle?.MaximumGrossWeight || vehicle?.Weight
      ),
      weight: safeString(vehicle?.Weight),
      seats: safeString(vehicle?.Seats),
      vehicleBodyTypeCode: safeString(
        vehicle?.VehicleBodyTypeCode || vehicle?.BodyType
      ),
      bodyType: safeString(vehicle?.BodyType),
      vehiclePowerTypeCode: safeString(
        vehicle?.VehiclePowerTypeCode || vehicle?.Fuel
      ),
      fuelType: safeString(vehicle?.Fuel),
      vehicleValue: safeString(vehicle?.Value),
      currency: "EUR",
      includeVAT: false,
      objectUsage: safeString(insurance?.ObjectUsage),
      vehicleTransportTypeCode: safeString(vehicle?.VehicleTransportTypeCode),
      abroadUse: safeString(insurance?.ObjectUsageAbroad),
      isLeased: safeBoolean(insurance?.ObjectLeased),
      leasingCompany: "",
      glassInsurance: "",
      deductible: "",
      theftDeductible: "",
      additionalEquipmentLimit: safeString(vehicle?.OtherEquipmentValue),
      replacementCar: false,
      accidentInsurance: false,
      repairLocation: "",
      usedInTraffic: true,
      technicalPassportNumber: safeString(
        vehicle?.RegistrationCertificateNumber
      ),
      ownerCode: safeString(owner?.Code || vehicle?.OwnerCode),
      ownerName: safeString(owner?.Name),
      ownerAddress: safeString(owner?.Address?.AddressString),
      ownerEmail: safeString(owner?.Address?.Email),
      ownerPhone: safeString(owner?.Address?.Phone),
      otherDriverAge: safeString(insurance?.DriverAge),
      otherDriverExperience: safeString(insurance?.DriverExperience),
      keyless: safeBoolean(vehicle?.IsKeyless),
      convertible: safeBoolean(vehicle?.IsConvertible),
      // Store raw Owner object for later submission (preserves all backend fields)
      _ownerRaw: owner || undefined,
    },
    clientData: {
      clientType: safeBoolean(client?.IsCompany) ? "company" : "person",
      vip: safeBoolean(client?.IsVip),
      sicCode:
        getTaxonomyCodeFromId(
          taxonomy,
          client?.PartnerMainSicCode,
          "SicCode"
        ) || "",
      resident: safeBoolean(client?.IsResident),
      bonusMalus: "",
      clientName: safeBoolean(client?.IsCompany)
        ? safeString(client?.Name)
        : "",
      clientFirstName: safeBoolean(client?.IsCompany)
        ? ""
        : safeString(client?.FirstName),
      clientLastName: safeBoolean(client?.IsCompany)
        ? ""
        : safeString(client?.LastName),
      clientCode: safeString(client?.Code),
      clientEmail: safeString(client?.Address?.Email),
      licenceDate: formatDate(client?.DriverLicenceDate),
      region: safeString(client?.RegionCode),
      clientAge: safeString(client?.Age),
      additionalRegion: "",
      drivingExperience: safeString(client?.Experience),
      city: safeString(client?.Address?.City),
      socialDiscount: safeString(client?.InvalidityCode),
      cityAddress: safeString(client?.Address?.Street),
      territoryOfValidity: safeString(insurance?.CoverageTerritory),
      address: safeString(client?.Address?.AddressString),
      isExistingInGOS: safeBoolean(client?.IsExistingInGOS),
      otherDriverLicenceDate: formatDate(insurance?.DriverLicenceDate),
      postIndex: safeString(client?.Address?.Index),
      otherDriverAge: safeString(insurance?.DriverAge),
      clientPhoneNumber: safeString(
        client?.Address?.Phone || client?.Address?.MobilePhone
      ),
      // Store raw Client object for later submission (preserves all backend fields)
      _clientRaw: client || undefined,
    },
    additionalData: {
      proposalValidTill: "",
      paymentType: "",
      policyDeliveryType: "",
      broker: "",
      accountManager: safeString(offer.OfferRequestedByUserFullName),
      salesPerson: "",
      responsiblePerson: safeString(offer.OfferRequestedByUserFullName),
      family: "",
      notes: "",
      lastCall: "",
      remindAutomatically: false,
      lastEmail: "",
      createdBy: safeString(offer.OfferRequestedByUserFullName),
      modifiedBy: safeString(offer.LastModifiedByUserFullName),
    },
    insuranceData: {
      previousInsuranceEndDate: request?.PreviousInsuranceEndDate
        ? formatDate(request.PreviousInsuranceEndDate)
        : "",
      // Use backend date if available and valid (for editing), otherwise use tomorrow (for new/duplicate)
      startDate: insurance?.StartDate
        ? formatDate(insurance.StartDate)
        : format(addDays(new Date(), 1), "yyyy-MM-dd"),
    },
  };
};

// ============================================================================
// SECTION 4: Form Data → API Requests (Calculations)
// ============================================================================

/**
 * Cleans client data based on client type before submission
 * Ensures only relevant fields are sent to the API
 */
export function cleanClientDataForSubmission(
  formData: ProposalFormData
): ProposalFormData {
  const cleanedData = { ...formData };

  if (cleanedData.clientData.clientType === "company") {
    // Clear person fields for company
    cleanedData.clientData.clientFirstName = "";
    cleanedData.clientData.clientLastName = "";
  } else {
    // Clear company field for person
    cleanedData.clientData.clientName = "";
  }

  return cleanedData;
}

/**
 * Simple mapping for basic calculation requests (minimal version)
 * Used by useProposalQuotes for Preview Quotes functionality
 * Returns minimal vehicle and driver info for quick calculations
 */
export function mapProposalFormToCalculationRequest(
  form: ProposalFormData
): any {
  const cleanedForm = cleanClientDataForSubmission(form);

  return {
    VehicleInfo: {
      VehicleLicensePlate: cleanedForm.vehicleData.licensePlateNumber,
      VehicleVin: cleanedForm.vehicleData.vin,
      OwnerCode: cleanedForm.vehicleData.ownerCode,
      RegistrationCertificateNumber:
        cleanedForm.vehicleData.technicalPassportNumber,
      VehicleBrand: cleanedForm.vehicleData.vehicleMake,
      VehicleModel: cleanedForm.vehicleData.vehicleModel,
      VehicleManufacturedOn: cleanedForm.vehicleData.productionYear,
      OwnerFirstName: cleanedForm.vehicleData.ownerName,
    },
    AdditionalCalculationInfo: {
      OwnerDrivingLicenseDate: cleanedForm.clientData.licenceDate,
      OwnerCity: cleanedForm.clientData.region
        ? Number(cleanedForm.clientData.region)
        : undefined,
      OtherDriverAge: cleanedForm.clientData.otherDriverAge
        ? Number(cleanedForm.clientData.otherDriverAge)
        : undefined,
    },
  };
}

/**
 * Comprehensive transformation for full calculation requests
 * Used by CascoQuotesTab and MtplQuotesTab for actual calculations
 *
 * @param formData - Form data from react-hook-form
 * @param taxonomy - Taxonomy for code-to-ID conversions
 * @param selectedInsurers - Array of selected insurer names
 * @param selectedProducts - Array of products (e.g., ['mtpl', 'casco'])
 * @param selectedMonthsOrPayments - Payment options
 * @param userInfo - Current user information for Common section
 */
export const transformProposalToCalculationRequest = (
  formData: any,
  taxonomy?: TaxonomyTaxonomyState | ProcessedTaxonomyState | null,
  selectedInsurers?: string[],
  selectedProducts?: string[],
  selectedMonthsOrPayments?: number[],
  userInfo?: UserInfo | null
): CalculationRequestDto => {
  // Clean client data before mapping
  const cleanedClientData = {
    ...formData.clientData,
    ...(formData.clientData.clientType === "company"
      ? { clientFirstName: "", clientLastName: "" }
      : { clientName: "" }),
  };

  const currentDate = new Date().toISOString();

  // Determine which products are selected
  const products = selectedProducts || [
    formData.proposalData.insuranceProduct?.toLowerCase() || "casco",
  ];

  // Get raw prefill data for Client and Owner (if available)
  const rawClient = formData.clientData?._clientRaw;
  const rawOwner = formData.vehicleData?._ownerRaw;

  const retVal = {
    SelectedProducts: products,
    // Add Mtpl object if MTPL is selected
    ...(products.includes("mtpl") && {
      Mtpl: {
        SelectedInsurers: selectedInsurers || [],
        SelectedMonthsOrPayments: selectedMonthsOrPayments || [],
      },
    }),
    // Add Casco object if CASCO is selected
    ...(products.includes("casco") && {
      Casco: {
        SelectedInsurers: selectedInsurers || [],
        SelectedMonthsOrPayments: selectedMonthsOrPayments || [],
        IncludeAdditionalConditions:
          formData.cascoSelections?.includeAdditionalConditions ?? true,
      },
    }),
    Common: {
      Language: "LV",
      CreatedTime: currentDate,
      Guid: genUUID(),
      UserName: userInfo?.userFullName || userInfo?.name || "Unknown User",
      Public: "false",
      Partner: "false",
      OfferStatusId: 2,
    },
    CustomParameters: "",
    // Previous insurance end date is only relevant for MTPL
    PreviousInsuranceEndDate: products.includes("mtpl")
      ? formData.insuranceData?.previousInsuranceEndDate || null
      : null,
    Client: {
      // Merge raw client data with form values
      ...(rawClient ?? {}),
      // Override with form values
      IsCompany: cleanedClientData.clientType === "company" ? "true" : "false",
      Code: cleanedClientData.clientCode ?? rawClient?.Code ?? "",
      Name:
        cleanedClientData.clientType === "company"
          ? cleanedClientData.clientName ?? ""
          : `${cleanedClientData.clientFirstName ?? ""} ${
              cleanedClientData.clientLastName ?? ""
            }`.trim(),
      FirstName:
        cleanedClientData.clientFirstName ?? rawClient?.FirstName ?? null,
      LastName: cleanedClientData.clientLastName ?? rawClient?.LastName ?? null,
      DriverLicenceDate:
        cleanedClientData.licenceDate ?? rawClient?.DriverLicenceDate ?? null,
      Experience:
        cleanedClientData.drivingExperience ?? rawClient?.Experience ?? null,
      Age: cleanedClientData.clientAge ?? rawClient?.Age ?? null,
      Region: rawClient?.Region ?? null,
      RegionCode: cleanedClientData.region ?? rawClient?.RegionCode ?? null,
      Address: {
        // Merge raw address data with form values
        ...(rawClient?.Address ?? {}),
        Country: rawClient?.Address?.Country ?? "LV",
        AddressString:
          cleanedClientData.address ??
          rawClient?.Address?.AddressString ??
          null,
        City: cleanedClientData.city ?? rawClient?.Address?.City ?? null,
        Street:
          cleanedClientData.cityAddress ?? rawClient?.Address?.Street ?? null,
        Index: cleanedClientData.postIndex ?? rawClient?.Address?.Index ?? null,
        Phone:
          cleanedClientData.clientPhoneNumber ??
          rawClient?.Address?.Phone ??
          null,
        Email:
          cleanedClientData.clientEmail ?? rawClient?.Address?.Email ?? null,
      },
      IsResident: cleanedClientData.resident ? "true" : "false",
      InvalidityCode:
        cleanedClientData.socialDiscount ?? rawClient?.InvalidityCode ?? null,
      IsVip: cleanedClientData.vip ? "true" : "false",
      PartnerMainSicCode:
        getTaxonomyIdFromCode(taxonomy, cleanedClientData.sicCode, "SicCode") ??
        rawClient?.PartnerMainSicCode ??
        null,
    },
    Vehicle: {
      LicensePlate: formData.vehicleData.licensePlateNumber ?? null,
      Vin: formData.vehicleData.vin ?? null,
      Brand: formData.vehicleData.vehicleMake ?? "",
      Model: formData.vehicleData.vehicleModel ?? "",
      RegistrationCertificateNumber:
        formData.vehicleData.technicalPassportNumber ?? null,
      PowerKw: formData.vehicleData.power ?? null,
      CubicCapacityCcm: formData.vehicleData.engineCapacity ?? null,
      Seats: formData.vehicleData.seats ?? null,
      Weight: formData.vehicleData.weight ?? null,
      MaximumGrossWeight: formData.vehicleData.maximumGrossWeight ?? null,
      ManufactureYear: formData.vehicleData.productionYear
        ? Number(formData.vehicleData.productionYear)
        : undefined,
      ManufactureDate: formData.vehicleData.productionYear
        ? `${formData.vehicleData.productionYear}-01-01T00:00:00.000Z`
        : null,
      OwnerCode: formData.vehicleData.ownerCode ?? null,
      TypeID:
        getTaxonomyIdFromCode(
          taxonomy,
          formData.vehicleData.objectGroupType,
          "VehicleType"
        )?.toString() ?? null,
      Type:
        getTaxonomyIdFromCode(
          taxonomy,
          formData.vehicleData.objectGroupType,
          "VehicleType"
        )?.toString() ?? null,
      TypeName: null,
      // Convert taxonomy codes to IDs (fields are named *Code but expect ID values)
      VehiclePowerTypeCode:
        getTaxonomyIdFromCode(
          taxonomy,
          formData.vehicleData.vehiclePowerTypeCode,
          "VehiclePowerType"
        )?.toString() ?? null,
      VehicleBodyTypeCode:
        getTaxonomyIdFromCode(
          taxonomy,
          formData.vehicleData.vehicleBodyTypeCode,
          "VehicleBodyType"
        )?.toString() ?? null,
      VehicleTransportTypeCode:
        getTaxonomyIdFromCode(
          taxonomy,
          formData.vehicleData.vehicleTransportTypeCode,
          "VehicleTransportType"
        )?.toString() ?? null,

      Value: formData.vehicleData.vehicleValue ?? "",
      Fuel:
        getTaxonomyIdFromCode(
          taxonomy,
          formData.vehicleData.vehiclePowerTypeCode,
          "VehiclePowerType"
        )?.toString() ?? null,
      IsNew: formData.vehicleData.isNew ? "true" : "false",
      Owner: {
        // Merge raw owner data with form values
        ...(rawOwner ?? {}),
        // Override with form values
        IsCompany: rawOwner?.IsCompany ?? "false",
        Code: formData.vehicleData.ownerCode ?? rawOwner?.Code ?? "",
        Name: formData.vehicleData.ownerName ?? rawOwner?.Name ?? "",
        FirstName: rawOwner?.FirstName ?? null,
        LastName: rawOwner?.LastName ?? null,
        IsResident: rawOwner?.IsResident ?? "true",
        Address: {
          // Merge raw address data with form values
          ...(rawOwner?.Address ?? {}),
          AddressString:
            formData.vehicleData.ownerAddress ??
            rawOwner?.Address?.AddressString ??
            null,
          Email:
            formData.vehicleData.ownerEmail ?? rawOwner?.Address?.Email ?? null,
          Phone:
            formData.vehicleData.ownerPhone ?? rawOwner?.Address?.Phone ?? null,
        },
      },
      Advertisements: "false",
      AlloyWheels: "false",
      AllWheelDrive: "false",
      AudioVideoEquipmentInstallation: "R",
      AudioVideoInstallation1: "false",
      AutomaticTransmission: "false",
      ChildSeat: "false",
      Conditioner: "None",
      CustomLicensePlate: "false",
      DetachableAudioFrontPanel: "false",
      FogLights: "false",
      GasEquipment: "false",
      GlassColouring: "false",
      IsConvertible: formData.vehicleData.convertible ? "true" : "false",
      IsKeyless: formData.vehicleData.keyless ? "true" : "false",
      LeatherInterior: "false",
      OtherEquipment1: "false",
      Platforms: "false",
      PurchaseValue: "0",
      RoofBoxes: "false",
      RoofHolders: "false",
      RoofRackMounts: "false",
      Tachograph: "false",
      TowHitch: "false",
      Tuning: "false",
    },
    Insurance: {
      Renewal: formData.proposalData.proposalType === "renewal",
      ObjectUsageAbroad: formData.vehicleData.abroadUse ?? "-1",
      CoverageTerritory: getTaxonomyIdFromCode(
        taxonomy,
        formData.clientData.territoryOfValidity,
        "CoverageTerritory"
      ),
      ObjectUsage: getTaxonomyIdFromCode(
        taxonomy,
        formData.vehicleData.objectUsage,
        "VehicleUsageTypeLV"
      ),
      StartDate: formData.insuranceData?.startDate ?? currentDate,
      FirstPaymentDate: formData.insuranceData?.startDate ?? currentDate,
      //ValidToDate: currentDate,
      Currency: "EUR", // Add currency field
      DriverAge: formData.vehicleData.otherDriverAge ?? null,
      DriverExperience: formData.vehicleData.otherDriverExperience ?? null,
      ObjectLeased: formData.vehicleData.isLeased,
    },
    VehicleSecurity: {
      Alarm: "false",
      Immobiliser: "false",
      ImmobiliserKeyCount: "0",
      ContactlessImmobiliser: "false",
      SecretSwitch: "false",
      TrackingSystem: "false",
      OtherSecuritySystems: "false",
      FactoryImmobiliserInKey: "false",
    },
  };

  return retVal as unknown as CalculationRequestDto;
};

// ============================================================================
// SECTION 5: Form Data → Save Request (Persisting Proposals)
// ============================================================================

/**
 * Get insurance products array from form data
 */
const getSelectedProducts = (formData: ProposalFormData): string[] => {
  const products: string[] = [];

  if (formData.proposalData.insuranceProduct) {
    products.push(formData.proposalData.insuranceProduct.toLowerCase());
  }

  return products.length > 0 ? products : ["mtpl"];
};

/**
 * Maps ProposalFormData to SaveProposalRequestDto for backend save API
 * Used when saving/updating proposals
 *
 * @param formData - Form data from react-hook-form
 * @param offerId - Optional existing offer ID for updates
 * @param taxonomy - Taxonomy for code-to-ID conversions
 */
export const mapProposalFormToSaveRequest = (
  formData: ProposalFormData,
  offerId?: string,
  taxonomy?: TaxonomyTaxonomyState | ProcessedTaxonomyState | null
): SaveProposalRequestDto => {
  const currentDate = new Date().toISOString();

  // Clean client data based on client type
  const cleanedClientData = {
    ...formData.clientData,
    ...(formData.clientData.clientType === "company"
      ? { clientFirstName: "", clientLastName: "" }
      : { clientName: "" }),
  };

  return {
    OfferId: offerId || undefined,
    Offer: {
      Request: {
        Client: {
          Name:
            cleanedClientData.clientType === "company"
              ? cleanedClientData.clientName || ""
              : `${cleanedClientData.clientFirstName || ""} ${
                  cleanedClientData.clientLastName || ""
                }`.trim(),
          Code: cleanedClientData.clientCode || "",
          IsCompany: cleanedClientData.clientType === "company",
          IsResident: safeBoolean(cleanedClientData.resident),
          Age: safeNumber(cleanedClientData.clientAge),
          Experience: safeNumber(cleanedClientData.drivingExperience),
          DriverLicenceDate: formatDateToISO(cleanedClientData.licenceDate),
          RegionCode: cleanedClientData.region || "",
          InvalidityCode: cleanedClientData.socialDiscount || "",
          IsVip: safeBoolean(cleanedClientData.vip),
          PartnerMainSicCode:
            getTaxonomyIdFromCode(
              taxonomy,
              cleanedClientData.sicCode,
              "SicCode"
            )?.toString() || undefined,
          Address: {
            Email: cleanedClientData.clientEmail || "",
            City: cleanedClientData.city || "",
            Street: cleanedClientData.cityAddress || "",
            AddressString: cleanedClientData.address || "",
            PostalCode: cleanedClientData.postIndex || "",
            Phone: cleanedClientData.clientPhoneNumber || "",
          },
        },
        Vehicle: {
          LicensePlate: formData.vehicleData.licensePlateNumber || "",
          Brand: formData.vehicleData.vehicleMake || "",
          Model: formData.vehicleData.vehicleModel || "",
          ManufactureYear:
            safeNumber(formData.vehicleData.productionYear) ?? null,
          IsNew: safeBoolean(formData.vehicleData.isNew),
          Vin: formData.vehicleData.vin || "",
          TypeName: formData.vehicleData.objectGroup || "",
          VehicleLegalTypeId:
            getTaxonomyIdFromCode(
              taxonomy,
              formData.vehicleData.objectGroup,
              "VehicleLegalType"
            ) ?? 900000001200063,
          VehicleTypeId:
            getTaxonomyIdFromCode(
              taxonomy,
              formData.vehicleData.objectGroupType,
              "VehicleType"
            ) ?? 2,
          CubicCapacityCcm: safeNumber(formData.vehicleData.engineCapacity),
          PowerKw: safeNumber(formData.vehicleData.power),
          Weight: null,
          MaximumGrossWeight: safeNumber(
            formData.vehicleData.maximumGrossWeight
          ),
          Seats: safeNumber(formData.vehicleData.seats),
          VehicleBodyTypeId: getTaxonomyIdFromCode(
            taxonomy,
            formData.vehicleData.vehicleBodyTypeCode,
            "VehicleBodyType"
          ),
          BodyType: formData.vehicleData.vehicleBodyTypeCode || "",
          VehiclePowerTypeId: getTaxonomyIdFromCode(
            taxonomy,
            formData.vehicleData.vehiclePowerTypeCode,
            "VehiclePowerType"
          ),
          Fuel: formData.vehicleData.vehiclePowerTypeCode || "",
          VehicleTransportTypeId: getTaxonomyIdFromCode(
            taxonomy,
            formData.vehicleData.vehicleTransportTypeCode,
            "VehicleTransportType"
          ),
          Value: safeNumber(formData.vehicleData.vehicleValue),
          OtherEquipmentValue: safeNumber(
            formData.vehicleData.additionalEquipmentLimit
          ),
          RegistrationCertificateNumber:
            formData.vehicleData.technicalPassportNumber || "",
          OwnerCode: formData.vehicleData.ownerCode || "",
          Owner: {
            Name: formData.vehicleData.ownerName || "",
            Code: formData.vehicleData.ownerCode || "",
            Address: {
              AddressString: formData.vehicleData.ownerAddress || "",
              Email: formData.vehicleData.ownerEmail || "",
              Phone: formData.vehicleData.ownerPhone || "",
            },
          },
        },
        Insurance: {
          Renewal: formData.proposalData.proposalType === "renewal",
          ObjectUsageAbroad: formData.vehicleData.abroadUse || "",
          CoverageTerritory: String(
            getTaxonomyIdFromCode(
              taxonomy,
              formData.clientData.territoryOfValidity,
              "CoverageTerritory"
            ) ?? 16
          ),
          Currency: formData.vehicleData.currency || "EUR",
          ValidFromDate: currentDate,
          ValidToDate:
            formatDateToISO(formData.additionalData.proposalValidTill) ||
            currentDate,
        },
        SelectedProducts: getSelectedProducts(formData),
      },
      ProposalMetadata: {
        ProposalNumber: formData.proposalData.proposalNumber || "",
        Status: formData.proposalData.proposalStatus || "draft",
        ProposalType: formData.proposalData.proposalType || "new",
        BulkTask: formData.proposalData.bulkTask || "",
        Template: formData.proposalData.proposalTemplate || "",
        RejectionReason: formData.proposalData.rejectionReason,
        WebTemplate: safeBoolean(formData.proposalData.webTemplate),
        ValidTill: formData.additionalData.proposalValidTill || "",
        PaymentType: formData.additionalData.paymentType || "",
        PolicyDeliveryType: formData.additionalData.policyDeliveryType || "",
        Broker: formData.additionalData.broker || "",
        AccountManager: formData.additionalData.accountManager || "",
        SalesPerson: formData.additionalData.salesPerson || "",
        ResponsiblePerson: formData.additionalData.responsiblePerson || "",
        Family: formData.additionalData.family || "",
        Notes: formData.additionalData.notes || "",
        LastCall: formData.additionalData.lastCall || "",
        RemindAutomatically: safeBoolean(
          formData.additionalData.remindAutomatically
        ),
        LastEmail: formData.additionalData.lastEmail || "",
        CreatedBy: formData.additionalData.createdBy || "",
        ModifiedBy: formData.additionalData.modifiedBy || "",
      },
    },
  };
};

/**
 * Validates required fields before saving
 * @returns Array of validation error messages
 */
export const validateProposalForSave = (
  formData: ProposalFormData
): string[] => {
  const errors: string[] = [];

  // Required client data based on client type
  const isCompany = formData.clientData.clientType === "company";
  if (isCompany) {
    if (!formData.clientData.clientName?.trim()) {
      errors.push("Client name is required");
    }
  } else {
    if (!formData.clientData.clientFirstName?.trim()) {
      errors.push("Client first name is required");
    }
    if (!formData.clientData.clientLastName?.trim()) {
      errors.push("Client last name is required");
    }
  }

  if (!formData.clientData.clientCode?.trim()) {
    errors.push("Client code is required");
  }

  // Required vehicle data
  if (!formData.vehicleData.licensePlateNumber?.trim()) {
    errors.push("License plate number is required");
  }
  if (!formData.vehicleData.vehicleMake?.trim()) {
    errors.push("Vehicle make is required");
  }
  if (!formData.vehicleData.vehicleModel?.trim()) {
    errors.push("Vehicle model is required");
  }

  return errors;
};

// ============================================================================
// SECTION 6: API Vehicle Info → Form Values (Prefill)
// ============================================================================

/**
 * Maps vehicle information from API response to form data structure.
 * Used for vehicle prefill functionality when user enters license plate, VIN, or owner code.
 *
 * **Prefill Coverage:**
 * - ✅ Vehicle basic info (plate, make, model, year, VIN)
 * - ✅ Vehicle technical specs (engine, power, weight, seats)
 * - ✅ Vehicle taxonomy codes (power type, transport type, body type, legal type, usage type)
 * - ✅ Vehicle value and currency
 * - ✅ Client basic info (code, name, type, resident status)
 * - ✅ Client contact info (email, phone, address)
 * - ✅ Client region code
 * - ✅ Policy coverage territory
 * - ✅ Insurance start date (Policy.EndDate + 1 day for MTPL, otherwise today + 1 day)
 * - ✅ Driver license date and experience (if available)
 *
 * **Intentionally Excluded Fields:**
 * - Insurance data (coverage, deductibles) - User must select
 * - Security systems (alarms, immobilizers) - Not in vehicle registry
 * - Additional equipment - Not in vehicle registry
 * - Leasing information - Requires separate verification
 * - Proposal metadata (status, notes, etc.) - Generated during save
 *
 * @param vehicleInfo - Vehicle and client information from `/vehicles/info` API
 * @param t - Translation function (optional, for error messages)
 * @param taxonomy - Taxonomy data (optional, for deriving parent relationships)
 * @param isMtplPolicy - Whether this is an MTPL policy (affects insurance start date calculation)
 * @returns Partial form values object for react-hook-form setValue with dot notation keys
 *
 * @example
 * ```ts
 * const vehicleInfo = await vehiclePrefillMutation.mutateAsync({ licensePlate: "AB1234" });
 * const formValues = mapVehicleInfoToFormValues(vehicleInfo, t, taxonomy, true);
 * Object.entries(formValues).forEach(([path, value]) => {
 *   setValue(path, value);
 * });
 * ```
 */
export function mapVehicleInfoToFormValues(
  vehicleInfo: any,
  t?: TFunction,
  taxonomy?: any,
  isMtplPolicy: boolean = false
): Record<string, any> {
  if (
    !vehicleInfo ||
    !vehicleInfo.Vehicle ||
    Object.keys(vehicleInfo.Vehicle).length === 0
  ) {
    return {};
  }

  const { Vehicle, Client } = vehicleInfo;
  const mappedValues: Record<string, any> = {};

  // Map basic vehicle data
  if (Vehicle.LicensePlate) {
    mappedValues["vehicleData.licensePlateNumber"] = Vehicle.LicensePlate;
  }
  if (Vehicle.Brand) {
    mappedValues["vehicleData.vehicleMake"] = Vehicle.Brand;
  }
  if (Vehicle.Model) {
    mappedValues["vehicleData.vehicleModel"] = Vehicle.Model;
  }
  if (Vehicle.ManufactureYear) {
    // Store as number for proper form handling
    mappedValues["vehicleData.productionYear"] = Number(
      Vehicle.ManufactureYear
    );
  }
  if (Vehicle.Vin) {
    mappedValues["vehicleData.vin"] = Vehicle.Vin;
  }
  if (Vehicle.PowerKw !== undefined && Vehicle.PowerKw !== null) {
    mappedValues["vehicleData.power"] = String(Vehicle.PowerKw);
  }
  if (
    Vehicle.CubicCapacityCcm !== undefined &&
    Vehicle.CubicCapacityCcm !== null
  ) {
    mappedValues["vehicleData.engineCapacity"] = String(
      Vehicle.CubicCapacityCcm
    );
  }
  if (Vehicle.Seats !== undefined && Vehicle.Seats !== null) {
    mappedValues["vehicleData.seats"] = String(Vehicle.Seats);
  }
  if (
    Vehicle.MaximumGrossWeight !== undefined &&
    Vehicle.MaximumGrossWeight !== null
  ) {
    mappedValues["vehicleData.maximumGrossWeight"] = String(
      Vehicle.MaximumGrossWeight
    );
  } else if (Vehicle.Weight !== undefined && Vehicle.Weight !== null) {
    mappedValues["vehicleData.maximumGrossWeight"] = String(Vehicle.Weight);
  }
  if (Vehicle.RegistrationCertificateNumber) {
    mappedValues["vehicleData.technicalPassportNumber"] =
      Vehicle.RegistrationCertificateNumber;
  }

  // Map taxonomy code fields for dropdown selections
  if (
    Vehicle.VehiclePowerTypeCode !== undefined &&
    Vehicle.VehiclePowerTypeCode !== null
  ) {
    mappedValues["vehicleData.vehiclePowerTypeCode"] = String(
      Vehicle.VehiclePowerTypeCode
    );
  }
  if (
    Vehicle.VehicleTransportTypeCode !== undefined &&
    Vehicle.VehicleTransportTypeCode !== null
  ) {
    mappedValues["vehicleData.vehicleTransportTypeCode"] = String(
      Vehicle.VehicleTransportTypeCode
    );
  }
  if (
    Vehicle.VehicleBodyTypeCode !== undefined &&
    Vehicle.VehicleBodyTypeCode !== null
  ) {
    mappedValues["vehicleData.vehicleBodyTypeCode"] = String(
      Vehicle.VehicleBodyTypeCode
    );
  }

  // Map VehicleTypeId (Object Group Type) and derive parent VehicleLegalType (Object Group)
  if (Vehicle.VehicleTypeId || Vehicle.TypeID) {
    const vehicleTypeId = Vehicle.VehicleTypeId || Vehicle.TypeID;
    mappedValues["vehicleData.objectGroupType"] = String(vehicleTypeId);

    // If taxonomy is available, look up the parent VehicleLegalType
    if (taxonomy?.VehicleType?.byId && taxonomy?.VehicleLegalType?.byId) {
      const vehicleType = taxonomy.VehicleType.byId[vehicleTypeId];
      if (vehicleType && vehicleType.parentId) {
        const parentLegalType =
          taxonomy.VehicleLegalType.byId[vehicleType.parentId];
        if (parentLegalType) {
          mappedValues["vehicleData.objectGroup"] = String(
            parentLegalType.code
          );
        }
      }
    }
  }

  // If VehicleLegalTypeId is explicitly provided, use it (overrides derived value)
  if (Vehicle.VehicleLegalTypeId) {
    mappedValues["vehicleData.objectGroup"] = String(
      Vehicle.VehicleLegalTypeId
    );
  }

  if (Vehicle.VehicleUsageType) {
    mappedValues["vehicleData.objectUsage"] = String(Vehicle.VehicleUsageType);
  }

  // Map owner data
  if (Vehicle.Owner?.Code || Vehicle.OwnerCode) {
    mappedValues["vehicleData.ownerCode"] =
      Vehicle.Owner?.Code || Vehicle.OwnerCode;
  }
  if (Vehicle.Owner?.Name) {
    mappedValues["vehicleData.ownerName"] = Vehicle.Owner.Name;
  }
  if (Vehicle.Owner?.Address?.AddressString) {
    mappedValues["vehicleData.ownerAddress"] =
      Vehicle.Owner.Address.AddressString;
  }
  if (Vehicle.Owner?.Address?.Email) {
    mappedValues["vehicleData.ownerEmail"] = Vehicle.Owner.Address.Email;
  }
  if (Vehicle.Owner?.Address?.Phone) {
    mappedValues["vehicleData.ownerPhone"] = Vehicle.Owner.Address.Phone;
  }

  // Store raw Owner object for later submission
  if (Vehicle.Owner) {
    mappedValues["vehicleData._ownerRaw"] = Vehicle.Owner;
  }

  // Map client data if available
  if (Client) {
    // Store raw Client object for later submission
    mappedValues["clientData._clientRaw"] = Client;

    if (Client.Code) {
      mappedValues["clientData.clientCode"] = Client.Code;
    }
    if (Client.IsCompany !== undefined) {
      mappedValues["clientData.clientType"] = Client.IsCompany
        ? "company"
        : "person";
    }
    if (Client.IsCompany) {
      if (Client.Name) {
        mappedValues["clientData.clientName"] = Client.Name;
      }
    } else {
      if (Client.FirstName) {
        mappedValues["clientData.clientFirstName"] = Client.FirstName;
      }
      if (Client.LastName) {
        mappedValues["clientData.clientLastName"] = Client.LastName;
      }
    }
    if (Client.Address?.Email) {
      mappedValues["clientData.clientEmail"] = Client.Address.Email;
    }
    if (Client.Address?.Phone) {
      mappedValues["clientData.clientPhoneNumber"] = Client.Address.Phone;
    }
    if (Client.Address?.AddressString) {
      mappedValues["clientData.address"] = Client.Address.AddressString;
    }
    if (Client.IsResident !== undefined) {
      mappedValues["clientData.resident"] = Client.IsResident;
    }
    if (Client.RegionCode) {
      mappedValues["clientData.region"] = String(Client.RegionCode);
    }
    if (Client.IsVip !== undefined) {
      mappedValues["clientData.vip"] = Client.IsVip;
    }
    if (Client.PartnerMainSicCode) {
      mappedValues["clientData.sicCode"] = Client.PartnerMainSicCode;
    }
  }

  // Map policy data if available
  const Policy = vehicleInfo.Policy;
  if (Policy) {
    // If policy has CoverageTerritory, map it to territoryOfValidity
    if (Policy.CoverageTerritory) {
      mappedValues["clientData.territoryOfValidity"] = String(
        Policy.CoverageTerritory
      );
    }

    // If policy has ObjectLeased, map it to isLeased
    if (Policy.ObjectLeased !== undefined && Policy.ObjectLeased !== null) {
      mappedValues["vehicleData.isLeased"] =
        Policy.ObjectLeased === true ||
        Policy.ObjectLeased === "true" ||
        Policy.ObjectLeased === "True";
    }

    // Map Policy.EndDate directly to previousInsuranceEndDate
    if (Policy.EndDate) {
      try {
        mappedValues["insuranceData.previousInsuranceEndDate"] = format(
          new Date(Policy.EndDate),
          "yyyy-MM-dd"
        );
      } catch (error) {
        console.warn("Failed to parse policy end date for previousInsuranceEndDate:", Policy.EndDate, error);
      }
    }

    // If policy has EndDate and is MTPL, calculate next day and set as insurance start date
    // Otherwise, use tomorrow
    if (Policy.EndDate && isMtplPolicy) {
      try {
        const endDate = new Date(Policy.EndDate);
        const nextDay = addDays(endDate, 1);
        const tomorrow = startOfDay(addDays(new Date(), 1));

        // Ensure the calculated date is at least tomorrow
        const calculatedDate = isAfter(nextDay, tomorrow) ? nextDay : tomorrow;

        // Format as YYYY-MM-DD for date input field
        mappedValues["insuranceData.startDate"] = format(
          calculatedDate,
          "yyyy-MM-dd"
        );
      } catch (error) {
        console.warn("Failed to parse policy end date:", Policy.EndDate, error);
        // Fallback to tomorrow if parsing fails
        mappedValues["insuranceData.startDate"] = format(
          addDays(new Date(), 1),
          "yyyy-MM-dd"
        );
      }
    } else {
      // For non-MTPL policies or when no EndDate is available, use tomorrow
      mappedValues["insuranceData.startDate"] = format(
        addDays(new Date(), 1),
        "yyyy-MM-dd"
      );
    }
  }

  return mappedValues;
}

// ============================================================================
// SECTION 7: Calculation Response → UI Display (Quote Cards)
// ============================================================================

/**
 * Maps calculation API response to quote card display format
 * Transforms complex nested response into flat structure for UI components
 *
 * @param response - Raw calculation response from backend
 * @returns Array of quote objects for display in quote cards
 */
export const mapCalculationResponseToQuotes = (response: any) => {
  if (!response?.Response?.Casco) {
    return [];
  }

  return response.Response.Casco.map((item: any) => {
    // Extract variants (different deductible/coverage options)
    const variants =
      item.Variants?.map((variant: any) => ({
        deductible: variant.Deductible,
        theftDeductible: variant.TheftDeductible,
        premium: variant.Premium,
        discountPremium: variant.DiscountPremium,
        currency: variant.Currency || "EUR",
        monthsOrPayments: variant.MonthsOrPayments,
      })) || [];

    // Get default premium (first variant or fallback)
    const defaultVariant = variants[0];
    const defaultPremium = defaultVariant?.premium || item.Premium || 0;
    const defaultInsurerPremium =
      defaultVariant?.discountPremium || item.InsurerPremium;
    const hasDiscount =
      defaultInsurerPremium && defaultInsurerPremium !== defaultPremium;

    // Extract error messages
    const errorList =
      item.ErrorList?.map((error: any) => ({
        message: error.Message,
        code: error.Code,
      })) || [];

    // Extract exception messages
    const exceptionList =
      item.ExceptionList?.map((exception: any) => ({
        genericMessage: exception.GenericMessage,
        code: exception.Code,
        genericStackTrace: exception.GenericStackTrace,
      })) || [];

    // Combine both error messages and exception messages for display
    const allErrorMessages = [
      ...errorList.map((e) => e.message),
      ...exceptionList.map((e) => e.genericMessage),
    ].filter(Boolean);

    const errorMessage =
      item.HasErrors || item.HasInsurerErrors
        ? allErrorMessages.join("; ")
        : undefined;

    return {
      insurer: item.Insurer || "Unknown",
      coverage: "comprehensive",
      premium: defaultPremium,
      discountPremium: hasDiscount ? defaultInsurerPremium : undefined,
      offerNumber: item.QuotingID?.toString(),
      isLoading: false,
      error: errorMessage,
      referenceNumber:
        response?.OfferId || response?.Offer?.Request?.CorrelationId,

      // Detailed information
      variants,
      conditions: item.Conditions,
      duration: item.Duration,
      hasErrors:
        item.HasErrors || errorList.length > 0 || exceptionList.length > 0,
      hasInsurerMessages: item.HasInsurerMessages || false,
      hasInsurerWarnings: item.HasInsurerWarnings || false,
      errorList,
      exceptionList,
      transportingExpenses: item.TransportingExpenses,
      territory: item.Territory,
      calculator: item.Calculator,
    };
  });
};

// ============================================================================
// Export all public functions
// ============================================================================

const proposalTransformers = {
  // Taxonomy helpers
  getTaxonomyIdFromCode,
  getTaxonomyCodeFromId,

  // Backend → Form
  mapOfferToProposalForm,
  mapVehicleInfoToFormValues,

  // Form → API
  cleanClientDataForSubmission,
  mapProposalFormToCalculationRequest,
  transformProposalToCalculationRequest,
  mapProposalFormToSaveRequest,
  validateProposalForSave,

  // Response → UI
  mapCalculationResponseToQuotes,
};

export default proposalTransformers;
